import React, { useState } from 'react';
import { Award, TrendingUp, TrendingDown, Download, FileText, Medal, Trophy } from 'lucide-react';

const examTypes = ['Mid-Term', 'Final', 'Unit Test 1', 'Unit Test 2'];

const resultsData = {
  'Mid-Term': {
    examDate: 'October 2025',
    totalMarks: 500,
    obtained: 437,
    percentage: 87.4,
    grade: 'A',
    rank: 5,
    totalStudents: 45,
    subjects: [
      { name: 'Mathematics', maxMarks: 100, obtained: 92, grade: 'A+', status: 'pass' },
      { name: 'Physics', maxMarks: 100, obtained: 88, grade: 'A', status: 'pass' },
      { name: 'Chemistry', maxMarks: 100, obtained: 85, grade: 'A', status: 'pass' },
      { name: 'English', maxMarks: 100, obtained: 90, grade: 'A+', status: 'pass' },
      { name: 'Biology', maxMarks: 100, obtained: 82, grade: 'A', status: 'pass' },
    ],
  },
  'Final': {
    examDate: 'March 2025',
    totalMarks: 500,
    obtained: 445,
    percentage: 89.0,
    grade: 'A+',
    rank: 3,
    totalStudents: 45,
    subjects: [
      { name: 'Mathematics', maxMarks: 100, obtained: 95, grade: 'A+', status: 'pass' },
      { name: 'Physics', maxMarks: 100, obtained: 90, grade: 'A+', status: 'pass' },
      { name: 'Chemistry', maxMarks: 100, obtained: 87, grade: 'A', status: 'pass' },
      { name: 'English', maxMarks: 100, obtained: 88, grade: 'A', status: 'pass' },
      { name: 'Biology', maxMarks: 100, obtained: 85, grade: 'A', status: 'pass' },
    ],
  },
  'Unit Test 1': {
    examDate: 'August 2025',
    totalMarks: 250,
    obtained: 215,
    percentage: 86.0,
    grade: 'A',
    rank: 7,
    totalStudents: 45,
    subjects: [
      { name: 'Mathematics', maxMarks: 50, obtained: 45, grade: 'A+', status: 'pass' },
      { name: 'Physics', maxMarks: 50, obtained: 42, grade: 'A', status: 'pass' },
      { name: 'Chemistry', maxMarks: 50, obtained: 40, grade: 'A', status: 'pass' },
      { name: 'English', maxMarks: 50, obtained: 44, grade: 'A+', status: 'pass' },
      { name: 'Biology', maxMarks: 50, obtained: 44, grade: 'A+', status: 'pass' },
    ],
  },
  'Unit Test 2': {
    examDate: 'December 2025',
    totalMarks: 250,
    obtained: 220,
    percentage: 88.0,
    grade: 'A',
    rank: 4,
    totalStudents: 45,
    subjects: [
      { name: 'Mathematics', maxMarks: 50, obtained: 47, grade: 'A+', status: 'pass' },
      { name: 'Physics', maxMarks: 50, obtained: 44, grade: 'A+', status: 'pass' },
      { name: 'Chemistry', maxMarks: 50, obtained: 42, grade: 'A', status: 'pass' },
      { name: 'English', maxMarks: 50, obtained: 45, grade: 'A+', status: 'pass' },
      { name: 'Biology', maxMarks: 50, obtained: 42, grade: 'A', status: 'pass' },
    ],
  },
};

const performanceTrend = [
  { exam: 'Unit Test 1', percentage: 86 },
  { exam: 'Mid-Term', percentage: 87.4 },
  { exam: 'Unit Test 2', percentage: 88 },
  { exam: 'Final', percentage: 89 },
];

const Results = () => {
  const [selectedExam, setSelectedExam] = useState('Mid-Term');
  const result = resultsData[selectedExam];

  const getGradeColor = (grade) => {
    if (grade.includes('A')) return 'text-green-600 bg-green-100';
    if (grade.includes('B')) return 'text-blue-600 bg-blue-100';
    if (grade.includes('C')) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Examination Results</h1>
          <p className="text-gray-600 mt-1">View your academic performance and grades</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
          <Download size={18} />
          <span>Download Report Card</span>
        </button>
      </div>

      {/* Exam Selector */}
      <div className="flex flex-wrap gap-2">
        {examTypes.map((exam) => (
          <button
            key={exam}
            onClick={() => setSelectedExam(exam)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              selectedExam === exam
                ? 'bg-primary-600 text-white shadow-md'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {exam}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-primary-100 text-sm">Total Marks</p>
              <p className="text-2xl font-bold mt-1">{result.obtained}/{result.totalMarks}</p>
            </div>
            <Award size={32} className="text-primary-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Percentage</p>
              <p className="text-2xl font-bold mt-1">{result.percentage}%</p>
            </div>
            <TrendingUp size={32} className="text-green-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Grade</p>
              <p className="text-2xl font-bold mt-1">{result.grade}</p>
            </div>
            <Medal size={32} className="text-purple-200" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100 text-sm">Class Rank</p>
              <p className="text-2xl font-bold mt-1">{result.rank}/{result.totalStudents}</p>
            </div>
            <Trophy size={32} className="text-amber-200" />
          </div>
        </div>
      </div>

      {/* Subject-wise Results */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Subject-wise Results</h2>
              <p className="text-sm text-gray-500 mt-1">{selectedExam} Examination • {result.examDate}</p>
            </div>
            <FileText size={20} className="text-gray-400" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Subject</th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-600">Max Marks</th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-600">Obtained</th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-600">Percentage</th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-600">Grade</th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {result.subjects.map((subject, index) => (
                <tr key={index} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <span className="font-medium text-gray-900">{subject.name}</span>
                  </td>
                  <td className="px-6 py-4 text-center text-gray-600">{subject.maxMarks}</td>
                  <td className="px-6 py-4 text-center font-semibold text-gray-900">{subject.obtained}</td>
                  <td className="px-6 py-4 text-center text-gray-600">
                    {((subject.obtained / subject.maxMarks) * 100).toFixed(1)}%
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getGradeColor(subject.grade)}`}>
                      {subject.grade}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                      Pass
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 font-semibold">
              <tr>
                <td className="px-6 py-4 text-gray-900">Total</td>
                <td className="px-6 py-4 text-center text-gray-900">{result.totalMarks}</td>
                <td className="px-6 py-4 text-center text-gray-900">{result.obtained}</td>
                <td className="px-6 py-4 text-center text-gray-900">{result.percentage}%</td>
                <td className="px-6 py-4 text-center">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getGradeColor(result.grade)}`}>
                    {result.grade}
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                    Pass
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Performance Trend */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Performance Trend</h2>
        <div className="flex items-end justify-between gap-4 h-48">
          {performanceTrend.map((item, index) => (
            <div key={index} className="flex-1 flex flex-col items-center">
              <div className="w-full bg-gray-100 rounded-t-lg relative" style={{ height: '160px' }}>
                <div
                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary-600 to-primary-400 rounded-t-lg transition-all duration-500"
                  style={{ height: `${item.percentage}%` }}
                />
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-sm font-semibold text-gray-700">
                  {item.percentage}%
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">{item.exam}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-green-600">
          <TrendingUp size={16} />
          <span>Your performance has improved by 3% over the year</span>
        </div>
      </div>

      {/* Grading Scale */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-4">Grading Scale</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <div className="text-center p-3 bg-white rounded-lg shadow-sm">
            <span className="text-lg font-bold text-green-600">A+</span>
            <p className="text-xs text-gray-500 mt-1">90% & above</p>
          </div>
          <div className="text-center p-3 bg-white rounded-lg shadow-sm">
            <span className="text-lg font-bold text-green-500">A</span>
            <p className="text-xs text-gray-500 mt-1">80% - 89%</p>
          </div>
          <div className="text-center p-3 bg-white rounded-lg shadow-sm">
            <span className="text-lg font-bold text-blue-600">B+</span>
            <p className="text-xs text-gray-500 mt-1">70% - 79%</p>
          </div>
          <div className="text-center p-3 bg-white rounded-lg shadow-sm">
            <span className="text-lg font-bold text-yellow-600">B</span>
            <p className="text-xs text-gray-500 mt-1">60% - 69%</p>
          </div>
          <div className="text-center p-3 bg-white rounded-lg shadow-sm">
            <span className="text-lg font-bold text-red-600">C</span>
            <p className="text-xs text-gray-500 mt-1">Below 60%</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Results;
